import moment from 'moment';

export const getTimeDifference = (start: number, end: number) => {
  const duration = moment.duration(moment(start).diff(end));

  if (!duration.isValid()) return;

  const hour = Math.floor(duration.asMinutes() / 60);
  const mins = duration.minutes().toString().padStart(2, '0');
  const secs = duration.seconds().toString().padStart(2, '0');

  return `${hour ? hour + ':' : ''}${mins}:${secs}`;
};

export const getTimeDifferenceInMinutes = (start: number, end: number) => {
  const duration = moment.duration(moment(start).diff(end));
  return duration.asMinutes();
};

export const convertToDoubleDigit = (input: string): string => {
  if (input.length == 1) {
    return `0${input}`;
  }
  return input;
};

export const minifyMinuteText = (text: string) => {
  return text.replace('minute', 'min');
};
export const minifyHourText = (text: string) => {
  return text.replace('hour', 'hr');
};
export const convertToMinutes = (time: string): string => {
  if (!time || typeof time !== 'string' || !time.includes(':')) {
    return 'Invalid time'; // Handle incorrect input
  }

  const parts = time.split(':').map(Number);

  // Ensure all parts are valid numbers (avoid NaN issues)
  if (parts.some(isNaN)) {
    return 'Invalid time';
  }

  let hours = 0,
    minutes = 0,
    seconds = 0;

  if (parts.length === 2) {
    // MM:SS format
    [minutes, seconds] = parts;
  } else if (parts.length === 3) {
    // HH:MM:SS format
    [hours, minutes, seconds] = parts;
  } else {
    return 'Invalid time';
  }

  const totalMinutes = Math.floor(hours * 60 + minutes + seconds / 60); // Include seconds

  if (totalMinutes < 1) {
    return 'Now'; // If less than 1 min, show "Now"
  }

  return `${totalMinutes} ${totalMinutes === 1 ? 'min' : 'mins'}`;
};

export const formatDueAtTime = (dueAt: number): string => {
  const currentTime = Date.now();
  const dueAtMoment = moment(dueAt);
  const currentMoment = moment(currentTime);

  // Check if dueAt is on the same day as today
  if (dueAtMoment.isSame(currentMoment, 'day')) {
    // Same day: show only time "12:30 PM"
    return dueAtMoment.format('h:mm A');
  } else {
    // Different day: show "31 Dec, 12:30 PM"
    return dueAtMoment.format('DD MMM, h:mm A');
  }
};

export const getStartTimeandEndTime = (
  startTime: string,
  fallbackToYesterday = false,
): { startTime: number; endTime: number } => {
  const now = moment();
  const todayMidnight = now.clone().startOf('day');

  let hour = 0;
  let minute = 0;

  if (startTime && startTime.includes(':')) {
    const [h, m] = startTime.split(':');
    const parsedHour = Number(h);
    const parsedMinute = Number(m);

    if (!isNaN(parsedHour)) hour = parsedHour;
    if (!isNaN(parsedMinute)) minute = parsedMinute;
  } else {
    // Invalid format or empty → fallback to 00:00 today
    const start = todayMidnight;
    const end = start
      .clone()
      .set({ hour: 23, minute: 59, second: 0, millisecond: 0 });
    return {
      startTime: start.valueOf(),
      endTime: end.valueOf(),
    };
  }

  let start = todayMidnight.clone().set({
    hour,
    minute,
    second: 0,
    millisecond: 0,
  });

  // If future and fallbackToYesterday true -> fallback to yesterday at same time
  if (start.isAfter(now) && fallbackToYesterday) {
    start = start.subtract(1, 'day');
  }

  const end = start
    .clone()
    .set({ hour: 23, minute: 59, second: 0, millisecond: 0 });

  return {
    startTime: start.valueOf(),
    endTime: end.valueOf(),
  };
};
